// apps/account-portal/src/pages/SystemOverviewPage.tsx
import React, { useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import SystemCard from '../components/SystemCard';
import PageFooter from '../components/PageFooter';
import { Alert } from '@redpanda/ui';
import { useTranslation } from 'react-i18next';
import { SystemType } from '@redpanda/types';
import { useSubscriptionStore } from '../stores/subscriptionStore';
import { ALL_SYSTEM_CONFIGS } from '../constants/systemDefinitions';
import PageContainer from '../components/PageContainer';
import Card from '../components/Card';
import Seo from '../components/Seo';
import LoginPromptBanner from '../components/LoginPromptBanner';

const SystemOverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const userContext = useContext(UserContext);
  const { t } = useTranslation();

  // 使用 subscriptionStore 取代本地狀態
  const subscriptionStore = useSubscriptionStore();
  const {
    subscriptions,
    subscriptionsLoaded,
    subscriptionsError: error,
    fetchAllSubscriptions
  } = subscriptionStore;

  // 從共用常數產生含翻譯文字的系統列表
  const allSystems = ALL_SYSTEM_CONFIGS.map(({ system, icon, featureKeys }) => ({
    system,
    icon,
    features: featureKeys.map(key => t(`systems.${system.toLowerCase()}.features.${key}`)),
  }));

  const isAuthenticated = userContext?.isAuthenticated ?? false;

  useEffect(() => {
    if (isAuthenticated) {
      fetchAllSubscriptions(true);
    }
  }, [isAuthenticated, fetchAllSubscriptions]);

  // 背景預載帳單資料（方案資料已在 App.tsx 登入時統一預載）
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!subscriptionStore.systemInvoicesLoaded) {
      subscriptionStore.fetchSystemInvoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // 檢查用戶是否訂閱了特定系統
  const getSubscription = (system: SystemType) => subscriptions.find(sub => sub.system === system);

  return (
    <PageContainer>
      <Seo
        title="RedPanda Tech 小熊貓科技 - 中小企業數位化專家 | CRM、專案管理、會計系統"
        description="RedPanda Tech 小熊貓科技 - 中小企業數位化專家。雲端 CRM、專案管理、會計系統，以及客製化軟體開發服務。"
        path="/"
      />
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
        <h1 className="text-2xl font-semibold text-[var(--text-primary)]">
          {t('overview.title')}
        </h1>
        {!isAuthenticated && <LoginPromptBanner />}
      </div>

      {error && (
        <Alert variant="error" title={t('common.status.error')} className="mb-6">
          {error}
        </Alert>
      )}

      {/* 卡片的標題、說明、功能都是固定文字，一律直接顯示；訂閱資料第一次載入時只有方案那一行顯示佔位（planLoading） */}
      <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
        {/* 外包服務卡片 - 放在第一位 */}
        <Card className="hover:[box-shadow:var(--card-shadow-hover)] transition-shadow duration-200 flex flex-col h-full">
          <div className="flex items-center mb-4 gap-2.5">
            <WrenchScrewdriverIcon className="h-6 w-6 flex-shrink-0 text-[var(--accent)]" />
            <h3 className="text-base font-semibold text-[var(--text-primary)] leading-snug">
              {t('systems.custom.name')}
            </h3>
          </div>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4">
            {t('systems.custom.description')}
          </p>
          {/* 重點清單緊接說明；flex-1 把按鈕推到卡片底部 */}
          <div className="mb-6 flex-1">
              <div className="flex flex-col gap-2">
                {[
                  t('systems.custom.features.item1'),
                  t('systems.custom.features.item2'),
                  t('systems.custom.features.item3')
                ].map((feature, index) => (
                  <div key={index} className="flex items-start gap-2 text-sm text-[var(--text-secondary)] text-balance">
                    <svg width="13" height="13" viewBox="0 0 20 20" className="flex-shrink-0 mt-1">
                      <polyline points="4,10 8,14 16,6" fill="none" stroke="var(--accent)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {feature}
                  </div>
                ))}
              </div>
          </div>
          <button
            onClick={() => navigate('/custom-service')}
            className="w-full bg-[var(--accent-fill)] text-[var(--accent-on)] hover:opacity-90 font-semibold py-2.5 px-4 rounded-md transition-opacity duration-150 text-sm"
          >
            {t('systems.custom.view_details')}
          </button>
        </Card>

        {/* 其他系統卡片 */}
        {allSystems.map(({ system, icon, features }) => {
          const subscription = getSubscription(system);
          return (
            <SystemCard
              key={system}
              system={system}
              isSubscribed={!!subscription}
              subscriptionStatus={subscription?.status}
              subscriptionEndDate={subscription ? new Date(subscription.endDate) : undefined}
              planName={subscription?.plan?.name}
              planId={subscription?.plan?.id}
              icon={icon}
              features={features}
              planLoading={!subscriptionsLoaded && !error}
            />
          );
        })}
      </div>

      <PageFooter />
    </PageContainer>
  );
};

export default SystemOverviewPage;
